import { ThunkDispatch } from "redux-thunk";
import {
  TemplateUIObject,
  TemplateTEMPObject,
  TemplateSTATEObject,
  tempTemplate,
  uiTemplate,
  stateTemplate,
  StateKeys,
  TempKeys,
  UIKeys,
} from "@services/storage/storeTemplate";
import StorageUtils from "@services/storage";
import { deepCompare } from "@utils/general";
import { AnyAction } from "@reduxjs/toolkit";
import { RootState, store } from "../../../store";
import { dispatchSetState } from "../../stateReducer";
import { dispatchSetTemp } from "../../tempReducer";
import { dispatchSetUI } from "../../uiReducer";
import { logger } from "@services/workers/helpers";

export type State = { [key: string]: any }; // your state type
export type AppDispatch = ThunkDispatch<State, any, AnyAction>;

export interface SetGeneric {
  (store: keyof RootState, name: keyof RootState[keyof RootState], body?: any): void;
}

export interface GetGeneric {
  (store: keyof RootState, name: keyof RootState[keyof RootState]): any;
}

export interface GenericAction {
  (store: string): TemplateUIObject | TemplateTEMPObject | TemplateSTATEObject | undefined;
}

export interface ActionTemp {
  (): TemplateTEMPObject;
}
export interface ActionUi {
  (): TemplateUIObject;
}
export interface ActionState {
  (): TemplateSTATEObject;
}

const namespace: string = "GenericActionStore";

const cachedStores: {
  temp: TemplateTEMPObject | null;
  ui: TemplateUIObject | null;
  state: TemplateSTATEObject | null;
} = {
  temp: null,
  ui: null,
  state: null,
};

const tempStore: ActionTemp = () => {
  if (cachedStores["temp"]) return cachedStores["temp"];
  logger.debug(namespace + "->" + tempStore.name, "Generating new TEMP Action Store");
  const object = tempTemplate();
  for (const key of Object.keys(object)) {
    Object.defineProperty(object, key, {
      get() {
        return getGeneric("temp", key as never);
      },
      set(value) {
        setGeneric("temp", key as never, value);
      },
    });
  }
  Object.defineProperty(object, "name", {
    get() {
      return "temp";
    },
  });
  cachedStores["temp"] = object;
  return object;
};

const uiStore: ActionUi = () => {
  if (cachedStores["ui"]) return cachedStores["ui"];
  logger.debug(namespace + "->" + uiStore.name, "Generating new UI Action Store");

  const object = uiTemplate();

  // Define properties for each key in the object
  for (const key of Object.keys(object)) {
    Object.defineProperty(object, key, {
      get() {
        return getGeneric("ui", key as never);
      },
      set(value) {
        setGeneric("ui", key as never, value);
      },
    });
  }

  // Define the 'name' property
  Object.defineProperty(object, "name", {
    get() {
      return "ui";
    },
  });

  cachedStores.ui = object;
  return cachedStores.ui;
};

const stateStore: ActionState = () => {
  if (cachedStores["state"]) return cachedStores["state"];
  logger.debug(namespace + "->" + stateStore.name, "Generating new STATE Action Store");
  const object = stateTemplate();
  for (const key of Object.keys(object)) {
    Object.defineProperty(object, key, {
      get() {
        return getGeneric("state", key as never);
      },

      set(value) {
        setGeneric("state", key as never, value);
      },
    });
  }
  Object.defineProperty(object, "name", {
    get() {
      return "state";
    },
  });
  cachedStores.state = object;
  return cachedStores.state;
};

export const getGeneric: GetGeneric = (storeName, name): any => {
  const value = store.getState()[storeName][name];
  return value;
};

export const setGeneric: SetGeneric = (storeName, name, payload) => {
  // save only if different
  if (!deepCompare(store.getState()[storeName][name], payload)) {
    setValueInStore(storeName, name, payload);
    StorageUtils.delayedWrite({ type: "local", store: storeName, key: name, value: payload, delay: 100 });
  }
};

export const setGenericNoDelay: SetGeneric = (storeName, name, payload) => {
  // save only if different
  if (!deepCompare(store.getState()[storeName][name], payload)) {
    setValueInStore(storeName, name, payload);
    StorageUtils.saveToStorage({ type: "local", store: storeName as any, key: name, value: payload });
  }
};

export const setGenericNoStorage: SetGeneric = (storeName, name, payload) => {
  // save only if different
  if (!deepCompare(store.getState()[storeName][name], payload)) {
    setValueInStore(storeName, name, payload);
  }
};

const setValueInStore = (storeName: string, name: StateKeys | TempKeys | UIKeys, payload: any) => {
  if (storeName === "state") {
    dispatchSetState(name as StateKeys, payload);
  }
  if (storeName === "temp") {
    dispatchSetTemp(name as TempKeys, payload);
  }
  if (storeName === "ui") {
    dispatchSetUI(name as UIKeys, payload);
  }
};

export const genericAction: GenericAction = (store) => {
  if (store === "temp") return tempStore();
  if (store === "ui") return uiStore();
  if (store === "state") return stateStore();
};
